import { Input } from "@hostingbv/lib/src/components/form/Input";
import { FC, FormEvent, useMemo, useState } from "react";
import { Button, IconButton } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";
import { useNavigate } from "react-router-dom";
import { config } from "../config";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Domain } from "@hostingbv/lib/src/api/partner/models/Domain";
import { useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { EntityNotesButton } from "../components/EntityNotesButton";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { faAdd, faGlobe, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";

export const Domains: FC = () => {
  const { partnerClient } = useApiClient(config);
  const { resource, loading, reload } = useFetchedResource(() => partnerClient.getAllDomains());
  useRefreshEffect(() => {
    reload();
  });
  const domains: Domain[] = useMemo(() => {
    if (resource?.success === true && resource.data) {
      return resource.data;
    }
    return [];
  }, [resource]);
  const { can } = usePermissions(partnerClient);
  return (
    <ContentContainer>
      <PageIconHeader icon={faGlobe}>
        <Breadcrumbs crumbs={[]} currentPage={"Domains"} />
        <PageHeader>Domains</PageHeader>
      </PageIconHeader>
      {can("create", "domains") && <SearchDomainForm />}
      <DomainOverview domains={domains} loading={loading} reloadDomains={() => reload()} />
    </ContentContainer>
  );
};

const SearchDomainForm: FC = () => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const lookupDomain = (e: FormEvent) => {
    e.preventDefault();
    navigate(`/domains/register/${search}`);
  };
  return (
    <Callout>
      <SectionHeader>Register new domain</SectionHeader>
      <Paragraph>Look up if a domain is available. It can then be registered, or transferred.</Paragraph>
      <form className={"flex items-end space-x-4"} onSubmit={lookupDomain}>
        <Input label={"Domain name"} type={"text"} placeholder={"example.com"} value={search} onChange={setSearch} />
        <Button submit size={"sm"} type={"primary"} text={"Check"} icon={faAdd} />
      </form>
    </Callout>
  );
};

const DomainOverview: FC<{ domains: Domain[]; loading: boolean; reloadDomains: () => void }> = (props) => {
  const navigate = useNavigate();
  const { getRealtimeStatus } = useRealtimeDeployments()!;
  return (
    <div className={"my-8"}>
      <SectionHeader>Registered domains</SectionHeader>
      <FilterableDataTable
        keyProperty={"id"}
        loading={props.loading}
        data={props.domains}
        searchableProperties={["displayName", (domain) => domain.customer?.displayName, "notes"]}
        actions={<IconButton icon={faRefresh} onClick={props.reloadDomains} type={"secondary"} size={"sm"} />}
        columns={[
                {
                  header: "Domain",
                  property: "displayName",
                  transform: (name, row) => (
                    <div className={"flex items-center space-x-2"}>
                      <span>{name}</span>
                      <EntityNotesButton allowNew={false} entity={row} />
                    </div>
                  ),
                  sort: true,
                },
                {
                  header: "Customer",
                  property: "customer",
                  transform: (customer) => customer?.displayName ?? "n/a",
                  sort: true,
                },
                {
                  header: "Status",
                  property: "deploymentStatus",
                  transform: (status, row) => <DeploymentStatusLabel status={getRealtimeStatus(row.id, status)} />,
                },
          // TODO: We need more information.
        ]}
        onClickRow={(row, e) => navigateRow(navigate, `/domains/${row.id}`, e)}
      />
    </div>
  );
};
